import { CircleAlert, Download, FileText, Film } from "lucide-react";
import { useEffect, useState } from "react";
import { PrismicMark } from "../components/Deliverables";
import { formatDate } from "../utils";
import { useT } from "../i18n";

/**
 * What somebody sees when a client sends them a link (backlog item 10).
 *
 * THIS PAGE HAS NO SESSION. It renders before the auth gate in App.tsx, outside
 * the shell — no sidebar, no month picker, no navigation into the product. A
 * stakeholder opening this is not a user of this system and must never be given a
 * doorway into one.
 *
 * NO MONEY APPEARS HERE, and it cannot: the endpoint does not send any. That is
 * enforced in server/share.ts by naming every public field explicitly, and held by
 * a test that greps the whole payload. This file could not print a credit figure
 * if it tried.
 *
 * An expired or revoked link gets the same page as a link that never existed —
 * one message, no detail. Anything else tells somebody probing tokens which ones
 * used to be real.
 */

type Share = {
  title: string;
  serviceName: string;
  serviceCategory: string;
  clientName: string;
  deliveredAt: string | null;
  files: Array<{ id: string; filename: string; mime: string; bytes: number; url: string }>;
  expiresAt: string;
};

export default function SharePage({ token }: { token: string }) {
  // No session here, so the language comes from the VISITOR's browser — see
  // readLanguage() in src/i18n. A stakeholder opening a link is not a user of
  // this product and has never chosen anything.
  const t = useT();
  const [share, setShare] = useState<Share | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "gone">("loading");

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/share/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error("gone");
        return (await r.json()).share as Share;
      })
      .then((data) => { if (!cancelled) { setShare(data); setState("ready"); } })
      // Every failure is the same failure to a visitor. There is nothing useful
      // for them in the difference, and something useful in it for an attacker.
      .catch(() => { if (!cancelled) setState("gone"); });
    return () => { cancelled = true; };
  }, [token]);

  if (state === "loading") {
    return <div className="share-page"><div className="share-card share-loading"><PrismicMark size={40} /></div></div>;
  }

  if (state === "gone" || !share) {
    return (
      <div className="share-page">
        <div className="share-card share-gone">
          <CircleAlert size={30} />
          <h1>{t("share.gone")}</h1>
          <p>{t("share.goneBody")}</p>
        </div>
      </div>
    );
  }

  const hero = share.files.find((f) => f.mime.startsWith("image/"));

  return (
    <div className="share-page">
      <div className="share-card">
        <header className="share-head">
          <PrismicMark size={26} />
          <div>
            {/* The client's name, because the stakeholder works for THEM — this is
                their campaign, delivered by us, not a Prismic advertisement. */}
            <span>{share.clientName}</span>
            <h1 dir="auto">{share.title}</h1>
            <em>{share.serviceName}{share.deliveredAt ? ` · ${formatDate(share.deliveredAt)}` : ""}</em>
          </div>
        </header>

        <div className="share-hero">
          {hero
            ? <img src={hero.url} alt="" />
            : <span className="share-hero-empty"><PrismicMark size={44} /></span>}
        </div>

        {share.files.length > 0 && (
          <ul className="share-files">
            {share.files.map((file) => (
              <li key={file.id}>
                {file.mime.startsWith("video/") ? <Film size={16} /> : <FileText size={16} />}
                <span dir="auto">{file.filename}</span>
                <em>{Math.max(1, Math.round(file.bytes / 1024))} KB</em>
                <a href={file.url} target="_blank" rel="noreferrer"><Download size={15} /> {t("share.open")}</a>
              </li>
            ))}
          </ul>
        )}

        {/* Said plainly. A link that stops working with no warning reads as a
            broken product rather than as a link that did its job. */}
        <footer className="share-foot">
          {t("share.stopsWorking", { date: formatDate(share.expiresAt) })}
        </footer>
      </div>
    </div>
  );
}
